import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { getCreatedToastMessage, getErrorToastMessage } from '@/libs/api'
import { QUERY_KEYS, STALE_TIME } from '@/libs/constants'
import type { HookParams, Vehicle, VehicleCustomerList, VehicleListParams } from '@/libs/interfaces'
import { vehiclesApi } from '@/services/api'

export function useCreateVehicles() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: vehiclesApi.create,
    onSuccess: (result) => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.vehicles.all,
      })
      toast.success(getCreatedToastMessage(result, t('vehicles.vehicleAdded')))
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}

export function useDeleteVehicles() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: vehiclesApi.delete,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.vehicles.all,
      })
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}

export function useInfiniteVehicles({ params, enabled }: HookParams<VehicleListParams>) {
  return useInfiniteQuery({
    queryKey: QUERY_KEYS.vehicles.infinite(params),
    queryFn: ({ pageParam }) =>
      vehiclesApi.listCursor({
        ...params,
        cursor: pageParam,
      }),
    getNextPageParam: (lastPage) => lastPage.pageInfo.next || undefined,
    getPreviousPageParam: (firstPage) => firstPage.pageInfo.prev || undefined,
    enabled: enabled ?? true,
    initialPageParam: undefined as string | undefined,
    staleTime: STALE_TIME['24Hours'], //24 Hours
  })
}

export function useUpdateVehicles() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: vehiclesApi.update,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.vehicles.all,
      })
    },
    onError: (error) => {
      toast.error(getErrorToastMessage(error, t('common.error')))
    },
  })
}

export function useVehicles({ params, enabled }: HookParams<VehicleListParams>) {
  return useQuery<VehicleCustomerList[]>({
    queryKey: QUERY_KEYS.vehicles.list(params),
    enabled: enabled ?? true,
    queryFn: async () => {
      return await vehiclesApi.list(params)
    },
    staleTime: STALE_TIME['1Hour'], //1 Hour
  })
}

export function useVehiclesByID({ params, enabled }: HookParams<Partial<Vehicle>>) {
  return useQuery<Vehicle>({
    queryKey: QUERY_KEYS.vehicles.id(params.driverCarID ?? ''),
    enabled: enabled ?? true,
    queryFn: async () => {
      return await vehiclesApi.getByID(params.orgID, params.driverCarID)
    },
    staleTime: STALE_TIME['24Hours'], // 24 hour
  })
}

export function useVehiclesCustomerMapping({ params, enabled }: HookParams<Partial<Vehicle>>) {
  return useQuery<Vehicle[]>({
    queryKey: QUERY_KEYS.vehicles.map(params),
    enabled: enabled ?? true,
    queryFn: async () => {
      return await vehiclesApi.getByCustomer(params.orgID, params.driverID)
    },
    staleTime: STALE_TIME['1Hour'], //30 mins
  })
}

// skipErrorRedirect: a 404 here means "no vehicle with this reg number yet" —
// an expected outcome the caller (NewVehicle) uses to switch into create
// mode, not an app-level failure.
export function useVehiclesGetByRegNumber({ params, enabled }: HookParams<Vehicle>) {
  return useQuery<Vehicle>({
    queryKey: QUERY_KEYS.vehicles.regNumber(params.driverCarRegistrationNumber || ''),
    enabled: enabled ?? true,
    queryFn: async () => {
      return await vehiclesApi.getByRegNumber(params.orgID, params.driverCarRegistrationNumber)
    },
    staleTime: STALE_TIME['1Hour'], //30 mins
    meta: { skipErrorRedirect: true },
  })
}
